import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { getThreadDetailPageUrl } from 'constants/pages';
import {
  CommentVoteEnum,
  GetGroupDiscussionsQuery,
  GroupCommentVotes,
  useGetGroupBySlugLazyQuery,
  useGetGroupDiscussionsAnonymousLazyQuery,
  useGetGroupDiscussionsLazyQuery,
  useGetUserGroupMembershipLazyQuery,
  useUpsertGroupThreadVoteMutation,
  useUpsertUsersGroupMembershipMutation,
} from 'types/generated/client';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { pluralize } from 'utils/shared/pluralize';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useAuthModals } from 'hooks/useAuthModals';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import ChatBubble from 'svg/ChatBubble';
import Photograph from 'svg/Photograph';
import Send from 'svg/Send';
import ThumbUp from 'svg/ThumbUp';
import SafeAreaPage from 'layouts/SafeAreaPage';
import { ButtonText, Button } from 'components/Button';
import Comment from 'components/Comment';
import NoDataPlaceholder from 'components/DiscoverPage/NoDataPlaceholder';
import ModalGroup from 'components/modals/ModalGroup';
import Head from 'components/utilities/Head';
import classNames from 'styles/utils/classNames';
import ModalCreateThread from '../Group/ModalCreateThread';

const ORIGINAL_COMMENT = 1;

export default function GroupDiscussionsPage() {
  const router = useRouter();
  const { groupSlug } = router.query;
  const { isUserSession, userId } = useViewer();
  const { user } = useGetCurrentUser();
  const { openSignupModal } = useAuthModals();
  const [isMember, setIsMember] = useState(false);
  const scrollBodyRef = useRef<HTMLDivElement>(null);

  const {
    isOpen: isModalCreateThreadOpen,
    openModal: openModalCreateThread,
    closeModal: closeModalCreateThread,
  } = useModal();

  const {
    isOpen: isJoinGroupModalOpen,
    openModal: openJoinGroupModal,
    closeModal: closeJoinGroupModal,
  } = useModal();

  const [getGroupBySlugQuery, { data: groupSlugData, loading: groupSlugLoading }] =
    useGetGroupBySlugLazyQuery();

  const [
    getGroupDiscussionsLazyQuery,
    { data: loggedInData, loading: loggedInLoading, refetch: refetchLoggedIn },
  ] = useGetGroupDiscussionsLazyQuery();
  const [
    getGroupDiscussionsAnonymousLazyQuery,
    { data: anonymousData, loading: anonymousLoading, refetch: refetchAnonymous },
  ] = useGetGroupDiscussionsAnonymousLazyQuery();

  const [upsertGroupThreadVoteMutation] = useUpsertGroupThreadVoteMutation();
  const [upsertUsersGroupMembershipMutation] = useUpsertUsersGroupMembershipMutation();
  const [getUserGroupMembershipLazyQuery, { data: userMembershipData }] =
    useGetUserGroupMembershipLazyQuery();

  const groupId = groupSlugData?.groups?.[0]?.id;

  const discussionsData = isUserSession ? loggedInData : anonymousData;

  const loading = groupSlugLoading || (isUserSession ? loggedInLoading : anonymousLoading);

  const groupData = groupSlugData?.groups?.[0];
  const isPrivate = groupData?.isPrivate;
  const threads = discussionsData?.groupThreads || [];
  const hasThreads = threads.length > 0;

  const refetch = () => {
    if (userId) {
      refetchLoggedIn();
    } else {
      refetchAnonymous();
    }
  };

  useEffect(() => {
    if (groupSlug && typeof groupSlug === 'string') {
      getGroupBySlugQuery({
        variables: {
          slug: groupSlug,
        },
      });
    }
  }, [groupSlug, getGroupBySlugQuery]);

  useEffect(() => {
    if (userId && groupId) {
      getUserGroupMembershipLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          userId,
          groupId,
        },
      });
    }
  }, [groupId, userId, getUserGroupMembershipLazyQuery]);

  useEffect(() => {
    if (userMembershipData?.groupMembers?.length && userMembershipData?.groupMembers[0]?.isActive) {
      setIsMember(true);
    } else {
      setIsMember(false);
    }
  }, [userMembershipData]);

  useEffect(() => {
    if (groupId) {
      if (isUserSession && userId) {
        getGroupDiscussionsLazyQuery({
          variables: {
            groupId,
            userId,
          },
        });
      } else {
        getGroupDiscussionsAnonymousLazyQuery({
          variables: {
            groupId,
          },
        });
      }
    }
  }, [
    groupId,
    isUserSession,
    userId,
    getGroupDiscussionsLazyQuery,
    getGroupDiscussionsAnonymousLazyQuery,
  ]);

  const handleJoinGroup = async () => {
    if (!isUserSession) {
      openSignupModal();
      return;
    }

    if (!userId || !groupId) return;

    try {
      await upsertUsersGroupMembershipMutation({
        variables: {
          userId,
          groupId,
          isActive: !isPrivate,
        },
        optimisticResponse: {
          __typename: 'mutation_root',
          insertGroupMembersOne: {
            __typename: 'GroupMembers',
            userId,
            groupId,
            isActive: !isPrivate,
          },
        },
      });

      getUserGroupMembershipLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          userId,
          groupId,
        },
      });
    } catch (error) {
      console.error('Error joining group:', error);
    }
  };

  const handleUpvote = async (commentId: string, userVote: GroupCommentVotes) => {
    if (!isUserSession) {
      openSignupModal();
      return;
    }

    if (isPrivate) {
      openJoinGroupModal();
      return;
    }

    if (!isMember) {
      await handleJoinGroup();
    }

    if (!userId) return;

    const isUserUpvoted = userVote?.vote === CommentVoteEnum.Positive;

    const variables = {
      groupThreadCommentId: commentId,
      userId,
      vote: isUserUpvoted ? CommentVoteEnum.None : CommentVoteEnum.Positive,
    };

    await upsertGroupThreadVoteMutation({
      variables,
      optimisticResponse: {
        __typename: 'mutation_root',
        insertGroupCommentVotesOne: {
          __typename: 'GroupCommentVotes',
          ...variables,
          id: userVote?.id || '',
        },
      },
    });

    if (isUserSession && userId && groupId) {
      refetch();
    }
  };

  const handleViewThread = (threadId: string) => {
    router.push(getThreadDetailPageUrl(groupSlug as string, threadId));
  };

  const refreshDiscussions = () => {
    if (groupId && isUserSession && userId) {
      refetch();
    }
  };

  const handleCommentClick = (threadId: string) => {
    router.push(getThreadDetailPageUrl(groupSlug as string, threadId));
  };

  const handleOpenCreateThreadModal = () => {
    if (!isUserSession) {
      openSignupModal();
      return;
    }
    if (isPrivate) {
      openJoinGroupModal();
      return;
    }

    if (!isMember) {
      handleJoinGroup();
    }

    openModalCreateThread();
  };

  return (
    <>
      <Head noIndex title={`${groupData?.title || 'Group'} Discussions`} />
      <SafeAreaPage isHideSidebar={true} isShowTopNav>
        <div className="relative flex h-full grow flex-col">
          <div className="flex h-full w-full grow flex-col">
            <h3 className="typography-product-heading-desktop mx-auto mt-4 w-full max-w-2xl">
              Discussions
            </h3>
            {isUserSession && (
              <div className="sticky top-10 flex items-center bg-color-bg-lightmode-primary px-4 pt-4 dark:bg-color-bg-darkmode-primary max-lg:top-12">
                <div className="mx-auto flex w-full max-w-2xl items-center border-y border-color-border-input-lightmode py-4 dark:border-color-border-input-darkmode">
                  <div className="shrink-0">
                    <img
                      className="h-8 w-8 rounded-full"
                      src={getProfileImageUrlOrPlaceholder({ path: user?.profileImagePath })}
                      alt="Profile"
                    />
                  </div>
                  <button
                    onClick={() => handleOpenCreateThreadModal()}
                    className="relative mx-2 h-[42px] max-h-20 w-full grow resize-none rounded-3xl border-none bg-color-bg-input-lightmode-primary px-3.5 py-1 text-left text-base text-color-text-lightmode-tertiary disabled:opacity-60 dark:bg-color-bg-input-darkmode-primary dark:text-color-text-darkmode-tertiary"
                  >
                    <span>Create a post</span>
                    <span className="absolute bottom-0 right-2 top-0 my-auto flex items-center">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert">
                        <Send className="h-3.5 w-3.5" />
                      </div>
                    </span>
                  </button>
                  <button className="shrink-0" onClick={() => openModalCreateThread()}>
                    <Photograph className="h-6 w-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                  </button>
                </div>
              </div>
            )}

            <main
              className="mx-auto w-full max-w-2xl space-y-6 px-4 pb-12 pt-4 lg:pb-24"
              ref={scrollBodyRef}
            >
              {loading ? (
                <div className="flex flex-col gap-4">
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      className="w-full animate-pulse rounded-lg bg-color-bg-lightmode-secondary p-4 dark:bg-color-bg-darkmode-secondary"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary"></div>
                        <div className="flex-1">
                          <div className="mb-2 h-4 w-1/4 rounded bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary"></div>
                          <div className="h-16 w-full rounded bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : hasThreads ? (
                <div className="flex flex-col gap-8">
                  {threads.map((thread) => {
                    const comment = thread.comments.find(
                      (comment) => comment.isOriginalThreadComment,
                    );
                    if (!comment) return null;

                    const userVote = isUserSession
                      ? (comment as GetGroupDiscussionsQuery['groupThreads'][0]['comments'][0])
                        ?.votes?.[0]
                      : null;
                    const isUserUpvoted = userVote?.vote === CommentVoteEnum.Positive;

                    return (
                      <div key={thread.id} className="flex flex-col">
                        <Comment
                          id={comment.id}
                          type="group"
                          content={comment.content}
                          senderName={comment?.userProfile?.fullName}
                          senderUsername={comment?.userProfile?.username}
                          senderProfileImageUrl={getProfileImageUrlOrPlaceholder({
                            path: comment?.userProfile?.profileImagePath,
                          })}
                          timestamp={comment.createdAt}
                          files={comment.files?.map((file) =>
                            getImageUrl({ url: file.url, path: file.path }),
                          )}
                        />
                        <div className="mt-4 flex items-center justify-between border-t border-color-border-input-lightmode pt-2 text-sm font-medium leading-4 dark:border-color-border-input-darkmode">
                          <ButtonText
                            onClick={() => handleUpvote(comment.id, userVote as GroupCommentVotes)}
                            className={classNames(
                              'flex items-center rounded-2xl px-2 py-1',
                              isUserUpvoted && 'bg-brand-fire-50 text-brand-fire-500',
                            )}
                          >
                            <ThumbUp className="mr-1 h-4 w-4" />{' '}
                            {comment?.votesAggregate?.aggregate?.count
                              ? comment?.votesAggregate?.aggregate?.count
                              : ''}
                          </ButtonText>
                          <ButtonText
                            className="py-2"
                            onClick={() => handleCommentClick(thread.id)}
                          >
                            {pluralize({
                              count: Math.max(
                                (thread?.commentsAggregate?.aggregate?.count || 0) -
                                ORIGINAL_COMMENT,
                                0,
                              ),
                              singular: 'Comment',
                              plural: 'Comments',
                            })}
                          </ButtonText>
                          <Button
                            variant="brand"
                            isInline
                            size="sm"
                            onClick={() => handleViewThread(thread.id)}
                            className="flex items-center py-2"
                            iconLeft={<ChatBubble className="mr-1 h-4 w-4" />}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-8">
                  <NoDataPlaceholder text="No discussions yet" />
                </div>
              )}
            </main>
          </div>
        </div>
      </SafeAreaPage>

      {/* Create Thread Modal */}
      {groupId && (
        <ModalCreateThread
          isOpen={isModalCreateThreadOpen}
          closeModal={closeModalCreateThread}
          userId={userId}
          groupId={groupId}
          isGroupMember={isMember}
          isGroupLoaded={!!groupData}
          handleCommentSubmit={async () => {
            refreshDiscussions();
          }}
        />
      )}
      <ModalGroup
        isOpen={isJoinGroupModalOpen}
        closeModal={closeJoinGroupModal}
        title="Request to join group"
        description="This is a private group. Comments and posts are restricted."
      />
    </>
  );
}
